import { CIType } from '../types';

/**
 * Maps a ServiceNow sys_class_name to a diagram CIType.
 * Used when importing CIs from ServiceNow relationships and when
 * loading persisted diagrams from localStorage.
 */
export const mapServiceNowClassToDiagramType = (sysClassName?: string, fallbackType: CIType = 'UNKNOWN_DEVICE'): CIType => {
  const normalizedClass = (sysClassName || '').toLowerCase().trim();
  if (!normalizedClass || normalizedClass === 'cmdb_ci') return fallbackType;

  // ── Web servers (check before generic server to avoid being swallowed) ──
  if (normalizedClass.includes('web_server') || normalizedClass.includes('webserver')) {
    return 'WEB_SERVER';
  }

  // ── Applications & Services ──
  if (
    normalizedClass.startsWith('cmdb_ci_appl') ||
    normalizedClass.startsWith('cmdb_ci_service') ||
    normalizedClass.includes('_app_') ||
    normalizedClass === 'cmdb_ci_business_app'
  ) {
    return 'APPLICATION';
  }

  // ── Databases (all variants: db_instance, db_mssql, db_ora, db_mysql, etc.) ──
  if (
    normalizedClass === 'cmdb_ci_database' ||
    normalizedClass === 'cmdb_ci_db_instance' ||
    normalizedClass.startsWith('cmdb_ci_db_') ||
    normalizedClass.includes('database') ||
    normalizedClass.includes('_db_') ||
    normalizedClass.includes('_sql') ||
    normalizedClass.includes('oracle') ||
    normalizedClass.includes('mysql') ||
    normalizedClass.includes('postgres') ||
    normalizedClass.includes('mongodb') ||
    normalizedClass.includes('_rdbms')
  ) {
    return 'DATABASE_INSTANCE';
  }

  // ── Servers (physical, virtual, OS-specific) ──
  if (
    normalizedClass === 'cmdb_ci_server' ||
    normalizedClass === 'cmdb_ci_win_server' ||
    normalizedClass === 'cmdb_ci_linux_server' ||
    normalizedClass === 'cmdb_ci_unix_server' ||
    normalizedClass === 'cmdb_ci_vm_instance' ||
    normalizedClass === 'cmdb_ci_computer' ||
    normalizedClass === 'cmdb_ci_mainframe' ||
    normalizedClass === 'cmdb_ci_esx_server' ||
    normalizedClass === 'cmdb_ci_hyper_v_server' ||
    normalizedClass.endsWith('_server') ||
    normalizedClass.includes('_host')
  ) {
    return 'VIRTUAL_MACHINE';
  }

  // ── VMware / vCenter / ESXi ──
  if (normalizedClass.includes('vcenter')) {
    return 'VCENTER_SERVER';
  }
  if (normalizedClass.includes('esx') || normalizedClass.includes('esxi')) {
    return 'ESXI_HOST';
  }

  // ── Firewalls ──
  if (normalizedClass.includes('firewall')) {
    return 'FIREWALL';
  }

  // ── Routers ──
  if (normalizedClass.includes('router')) {
    return 'ROUTER';
  }

  // ── Switches (including vendor-specific like cmdb_ci_netgear) ──
  if (
    normalizedClass.includes('switch') ||
    normalizedClass === 'cmdb_ci_netgear' ||
    normalizedClass.includes('_net_') ||
    normalizedClass === 'cmdb_ci_ip_switch'
  ) {
    return 'SWITCH';
  }

  // ── Load Balancers ──
  if (normalizedClass.includes('load_balancer') || normalizedClass === 'cmdb_ci_lb' || normalizedClass.includes('_lb_')) {
    return 'LOAD_BALANCER';
  }

  // ── Printers ──
  if (normalizedClass.includes('printer')) {
    return 'NETWORK_PRINTER';
  }

  // ── Storage ──
  if (normalizedClass.includes('storage') || normalizedClass.includes('_san') || normalizedClass.includes('_nas')) {
    return 'STORAGE_ARRAY';
  }

  // ── Wireless ──
  if (normalizedClass.includes('wireless') || normalizedClass.includes('_wap') || normalizedClass.includes('access_point')) {
    return 'WAP';
  }

  // ── Broad catch-all for remaining VMs/computers ──
  if (normalizedClass.includes('vm_') || normalizedClass.includes('computer') || normalizedClass.includes('host')) {
    return 'VIRTUAL_MACHINE';
  }

  // ── Network gear catch-all (cmdb_ci_ip_*, cmdb_ci_net_*) ──
  if (normalizedClass.startsWith('cmdb_ci_ip_') || normalizedClass.startsWith('cmdb_ci_net_')) {
    return 'SWITCH';
  }

  // ── Hardware catch-all ──
  if (normalizedClass.includes('hardware') || normalizedClass === 'cmdb_ci_comm') {
    return 'PHYSICAL_SERVER';
  }

  console.warn(`⚠️ Unmapped ServiceNow class: "${sysClassName}" → falling back to ${fallbackType}`);
  return fallbackType;
};
